import { getPrivateKey } from '@enuan/chat__utils';
import {
	useDidUpdateEffect,
	useUpdatedState,
} from '@avalenti89/react-ts-utils';
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { StoredKey } from 'src/utils/enuanLocalStorage';
import { configContext, IConfigContext } from './context';
import { configurationContext } from '../configuration/context';
import { useEnuanLocalStorage } from 'src/hooks/useEnuanLocalStorage';

export const ConfigProvider = ({
	children,
	baseUrl,
}: React.PropsWithChildren<Pick<IConfigContext, 'baseUrl'>>) => {
	const configuration = useContext(configurationContext);
	const privateKey = useMemo(
		() => getPrivateKey(configuration.logger_key),
		[configuration.logger_key],
	);

	const { get, set } = useEnuanLocalStorage();
	const localLocale = useMemo(() => get(StoredKey.LOCALE), [get]);
	const confLocale = configuration.localization?.locale;
	const defaultLocale = localLocale ?? confLocale ?? 'en';

	const [locale, _setLocale] = useState(defaultLocale);
	useDidUpdateEffect(() => {
		_setLocale(defaultLocale);
	}, [defaultLocale]);
	const setLocale = useCallback<typeof _setLocale>(
		(locale) => {
			set(StoredKey.LOCALE, locale, undefined);

			_setLocale(locale);
		},
		[set],
	);

	const [isInputLocked, setLockInput] =
		useState<IConfigContext['isInputLocked']>(false);

	const [survey, setSurvey] = useUpdatedState<IConfigContext['survey']>(
		configuration.survey?.enabled,
	);

	const [channelState, setChannelState] =
		useState<IConfigContext['channelState']>();

	const isRecordAudioEnabled =
		(configuration.speech_input || configuration.transcript_input) ?? false;
	const [isRecordAudioApproved, setIsRecordAudioApproved] = useState<boolean>();
	const handlePermissionStateChange = useCallback((state: PermissionState) => {
		if (state === 'granted') {
			setIsRecordAudioApproved(true);
		} else if (state === 'denied') {
			setIsRecordAudioApproved(false);
		} else {
			setIsRecordAudioApproved(undefined);
		}
	}, []);
	useEffect(() => {
		if (!isRecordAudioEnabled || !navigator.permissions) return;

		navigator.permissions
			.query({ name: 'microphone' as PermissionName })
			.then(({ state, onchange }) => {
				console.log('Permission state:', state);
				handlePermissionStateChange(state);
				onchange = () => {
					handlePermissionStateChange(state);
				};
			});
	}, [handlePermissionStateChange, isRecordAudioEnabled]);

	const configContextValue: IConfigContext = useMemo(
		() => ({
			baseUrl,
			channelState,
			setChannelState,
			survey,
			setSurvey,
			locale,
			setLocale,
			isInputLocked,
			setLockInput,
			isRecordAudioEnabled,
			isRecordAudioApproved,
			setIsRecordAudioApproved,
			privateKey,
		}),
		[
			baseUrl,
			channelState,
			isInputLocked,
			isRecordAudioApproved,
			isRecordAudioEnabled,
			locale,
			privateKey,
			setLocale,
			setSurvey,
			survey,
		],
	);
	if (!configuration) return null;

	return (
		<configContext.Provider value={configContextValue}>
			{children}
		</configContext.Provider>
	);
};
